<!--
  Formules & details complementaires (moteur de tarification STS).
  Port web de frmSTS.vb : ouvert en popup depuis NewFSE.vue.

  - 4 onglets : Donnees patient / Donnees facture / Formules / Erreurs (affiches
    seulement s'ils contiennent des donnees, cf indicateurs()).
  - Liste des actes a gauche (filtre par prestation) pour Facture / Formules.
  - Grilles Groupe | Champ | Modifiable | Valeur (valeur editable selon le flag).
  - Combo de choix de formule AMC par acte.
  - Donnees issues du backend STS (src/script/STS.js -> getResultatsSTS...),
    parsees par src/script/STSResultats.js.
-->
<template>
    <v-card class="formules-sts">
        <v-card-title class="d-flex align-center">
            <span>Formules &amp; details complementaires (STS)</span>
            <v-spacer></v-spacer>
            <v-btn icon="mdi-refresh" size="small" variant="text" title="Recalculer" @click="charger"></v-btn>
            <v-btn icon="mdi-close" size="small" variant="text" @click="annuler"></v-btn>
        </v-card-title>

        <v-card-text>
            <!-- Chargement / indisponibilite -->
            <div v-if="chargement" class="etat-info">Calcul de la tarification STS en cours...</div>
            <div v-else-if="erreurChargement" class="etat-erreur">
                Donnees STS indisponibles : {{ erreurChargement }}
                <div class="text-caption mt-1">
                    Le moteur STS necessite une facture chargee + le backend STS initialise.
                </div>
            </div>
            <div v-else-if="!resultats.length" class="etat-info">
                Aucun resultat de tarification STS (rien a afficher).
            </div>

            <template v-else>
                <!-- Onglets (seulement ceux qui ont des donnees) -->
                <v-tabs v-model="ongletActif" color="#119393" density="compact">
                    <v-tab v-if="ind.patient" value="patient">Donnees patient</v-tab>
                    <v-tab v-if="ind.facture" value="facture">Donnees facture</v-tab>
                    <v-tab v-if="ind.formules" value="formules">Formules</v-tab>
                    <v-tab v-if="ind.erreurs" value="erreurs">Erreurs</v-tab>
                </v-tabs>

                <!-- Liste des actes (filtre), masquee sur patient / erreurs -->
                <div v-if="ongletActif === 'facture' || ongletActif === 'formules'" class="mt-3">
                    <select v-model.number="acteSelectionne" class="champ-input champ-select">
                        <option v-for="a in actes" :key="a.noPresta" :value="a.noPresta">{{ a.label }}</option>
                    </select>
                </div>

                <!-- Onglet Patient -->
                <div v-show="ongletActif === 'patient'" class="mt-3">
                    <table class="grille-sts" v-if="rowsPatient.length">
                        <thead><tr><th>Groupe</th><th>Champ</th><th>Modif.</th><th>Valeur</th></tr></thead>
                        <tbody>
                            <tr v-for="(r, i) in rowsPatient" :key="'p' + i">
                                <td class="col-groupe">{{ r.groupe }}</td>
                                <td>{{ r.champ }}</td>
                                <td class="col-modif">{{ r.modifiable }}</td>
                                <td><component :is="'span'" v-if="!r.editable">{{ r.valeur }}</component>
                                    <input v-else class="champ-cellule" :value="r.valeur"
                                           @change="onModif(r, $event.target.value)" /></td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="etat-info">Aucune donnee patient.</div>
                </div>

                <!-- Onglet Facture -->
                <div v-show="ongletActif === 'facture'" class="mt-3">
                    <table class="grille-sts" v-if="rowsFacture.length">
                        <thead><tr><th>Groupe</th><th>Champ</th><th>Modif.</th><th>Valeur</th></tr></thead>
                        <tbody>
                            <tr v-for="(r, i) in rowsFacture" :key="'f' + i">
                                <td class="col-groupe">{{ r.groupe }}</td>
                                <td>{{ r.champ }}</td>
                                <td class="col-modif">{{ r.modifiable }}</td>
                                <td><span v-if="!r.editable">{{ r.valeur }}</span>
                                    <input v-else class="champ-cellule" :value="r.valeur"
                                           @change="onModif(r, $event.target.value)" /></td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="etat-info">Aucune donnee facture pour cette selection.</div>
                </div>

                <!-- Onglet Formules -->
                <div v-show="ongletActif === 'formules'" class="mt-3">
                    <div v-if="choix.options.length" class="mb-3">
                        <label class="font-weight-bold">Formule a appliquer</label>
                        <select v-model="formuleSelectionnee" class="champ-input champ-select mt-1"
                                @change="onChoixFormule">
                            <option value="">(Aucune)</option>
                            <option v-for="o in choix.options" :key="o.value" :value="o.value">{{ o.label }}</option>
                        </select>
                        <span v-if="calculTheorique" class="ml-3 text-body-2">
                            Calcul theorique : <strong>{{ calculTheorique }}</strong>
                        </span>
                    </div>
                    <table class="grille-sts" v-if="rowsFormules.length">
                        <thead><tr><th>Groupe</th><th>Champ</th><th>Modif.</th><th>Valeur</th></tr></thead>
                        <tbody>
                            <tr v-for="(r, i) in rowsFormules" :key="'fo' + i">
                                <td class="col-groupe">{{ r.groupe }}</td>
                                <td>{{ r.champ }}</td>
                                <td class="col-modif">{{ r.modifiable }}</td>
                                <td><span v-if="!r.editable">{{ r.valeur }}</span>
                                    <input v-else class="champ-cellule" :value="r.valeur"
                                           @change="onModif(r, $event.target.value)" /></td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="etat-info">Aucune formule pour cette selection.</div>
                </div>

                <!-- Onglet Erreurs -->
                <div v-show="ongletActif === 'erreurs'" class="mt-3">
                    <table class="grille-sts" v-if="rowsErreurs.length">
                        <thead><tr><th>Code</th><th>Prestation</th><th>Libelle de l'erreur</th></tr></thead>
                        <tbody>
                            <tr v-for="(e, i) in rowsErreurs" :key="'e' + i" :style="{ backgroundColor: e.couleur }">
                                <td>{{ e.code }}</td>
                                <td>{{ e.prestation }}</td>
                                <td>{{ e.libelle }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="etat-info">Aucune erreur.</div>
                </div>
            </template>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="annuler">Annuler</v-btn>
            <v-btn color="#119393" :loading="validation" :disabled="!resultats.length" @click="valider">Valider</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import { defineComponent } from 'vue';
    import STS from '../script/STS.js';
    import {
        tablePatient, tableFacture, tableFormules, tableErreurs,
        choixFormules, listeActes, indicateurs,
    } from '../script/STSResultats.js';

    export default defineComponent({
        emits: ['close'],

        data() {
            return {
                resultats: [],
                nbResultats: 0,
                chargement: false,
                erreurChargement: '',
                validation: false,
                ongletActif: 'patient',
                acteSelectionne: 0,
                formuleSelectionnee: '',
                calculTheorique: '',
            };
        },

        computed: {
            ind() { return indicateurs(this.resultats); },
            actes() { return listeActes(this.resultats); },
            rowsPatient() { return tablePatient(this.resultats); },
            rowsFacture() { return tableFacture(this.resultats, this.acteSelectionne); },
            rowsFormules() { return tableFormules(this.resultats, this.acteSelectionne); },
            rowsErreurs() { return tableErreurs(this.resultats, this.actes); },
            choix() { return choixFormules(this.resultats, this.acteSelectionne); },
        },

        watch: {
            // Met a jour la formule selectionnee quand on change d'acte.
            choix: {
                handler(val) { this.formuleSelectionnee = val.selected || ''; },
                immediate: true,
            },
        },

        methods: {
            // Choisit l'onglet par defaut : erreurs si graves, sinon 1er onglet dispo.
            choisirOnglet() {
                const i = this.ind;
                if (i.erreurs && i.erreursGraves) { this.ongletActif = 'erreurs'; return; }
                if (i.patient) this.ongletActif = 'patient';
                else if (i.facture) this.ongletActif = 'facture';
                else if (i.formules) this.ongletActif = 'formules';
                else if (i.erreurs) this.ongletActif = 'erreurs';
            },

            async charger() {
                this.chargement = true;
                this.erreurChargement = '';
                try {
                    const r = await STS.getResultats();
                    this.resultats = (r && (r.resultats ?? r.Resultats)) || [];
                    this.nbResultats = (r && (r.nbResultats ?? r.NbResultats)) || this.resultats.length;
                    if (r && r.ok === false && r.message) this.erreurChargement = r.message;
                    this.choisirOnglet();
                } catch (e) {
                    console.error('Erreur chargement resultats STS :', e);
                    this.erreurChargement = (e && e.message) ? e.message : 'backend injoignable';
                    this.resultats = [];
                } finally {
                    this.chargement = false;
                }
            },

            // Met a jour les resultats locaux depuis une reponse backend.
            majDepuisReponse(r) {
                if (!r) return;
                const res = r.resultats ?? r.Resultats;
                if (Array.isArray(res)) {
                    this.resultats = res;
                    this.nbResultats = (r.nbResultats ?? r.NbResultats) || res.length;
                }
            },

            async onModif(row, nouvelleValeur) {
                try {
                    const r = await STS.setModif({
                        IndexResultat: row.indexResultat,
                        NoChamp: row.noChamp,
                        Valeur: String(nouvelleValeur),
                    });
                    this.majDepuisReponse(r);
                } catch (e) {
                    console.error('Erreur modification champ STS :', e);
                }
            },

            async onChoixFormule() {
                try {
                    const r = await STS.choixFormule({
                        NoPresta: this.acteSelectionne,
                        NoFormule: this.formuleSelectionnee,
                    });
                    this.calculTheorique = (r && (r.calculTheorique ?? r.CalculTheorique)) || '';
                    this.majDepuisReponse(r);
                } catch (e) {
                    console.error('Erreur choix formule STS :', e);
                }
            },

            async valider() {
                this.validation = true;
                try {
                    await STS.valider();
                    this.$emit('close');
                } catch (e) {
                    console.error('Erreur validation STS :', e);
                } finally {
                    this.validation = false;
                }
            },

            annuler() { this.$emit('close'); },
        },

        mounted() { this.charger(); },
    });
</script>

<style scoped>
    * { color: #434343; }
    .formules-sts { padding-bottom: 6px; }

    /* Onglets : meme correctif que GestionComplementaire (sur mobile le dialog
       contraint en hauteur ecrasait les v-tabs a 0 -> invisibles). On empeche le
       shrink des onglets et on laisse le corps scroller. Sans media query (le
       WebView MAUI les ignore en largeur, cf memoire). */
    .formules-sts :deep(.v-tabs) { flex: 0 0 auto; }
    .formules-sts :deep(.v-card-text) { flex: 1 1 auto; min-height: 0; overflow-y: auto; }

    .etat-info { color: #777; padding: 16px 4px; }
    .etat-erreur { color: #E8A33D; font-weight: bold; padding: 12px 4px; }

    .champ-input {
        padding: 8px 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 0.95rem;
        outline: none;
    }
    .champ-input:focus { border-color: #119393; }
    .champ-select { min-width: 280px; background: #fff; }

    .grille-sts {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
    }
    .grille-sts th, .grille-sts td {
        border: 1px solid #e0e0e0;
        padding: 5px 8px;
        text-align: left;
        vertical-align: top;
    }
    .grille-sts thead th {
        background: #f0f0f0;
        font-weight: bold;
    }
    .col-groupe { color: #119393; font-weight: bold; white-space: nowrap; }
    .col-modif { width: 70px; text-align: center; color: #888; }

    .champ-cellule {
        width: 100%;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 3px 6px;
        font-size: 0.85rem;
        outline: none;
    }
    .champ-cellule:focus { border-color: #119393; }

    @media (max-width: 800px) {
        .champ-select { min-width: 100%; }
        .grille-sts { font-size: 0.78rem; }
    }
</style>
